import {
  Component,
  OnInit,
  computed,
  effect,
  input,
  signal,
  untracked,
} from '@angular/core';
import { FieldTree, FormField, disabled, form } from '@angular/forms/signals';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import {
  DateTimeParts,
  composeDateTimeValue,
  formatDateTimeValue,
  toDateTimeParts,
} from './date-time-input.value';

@Component({
  selector: 'app-date-time-input',
  imports: [FormField, NzDatePickerModule, NzTimePickerModule],
  templateUrl: './date-time-input.component.html',
  // Keeps a caller's static `id` attribute off the host element, which would
  // otherwise duplicate the `id` the template below puts on the first picker.
  host: { '[attr.id]': 'null' },
})
export class DateTimeInputComponent implements OnInit {
  readonly field = input.required<FieldTree<string | null>>();
  readonly id = input('');
  readonly label = input('');
  readonly hideLabel = input(false);
  readonly containerClass = input('');
  readonly placeholder = input('');
  readonly dateField = input(true);
  readonly timeField = input(false);

  protected readonly state = computed(() => this.field()());

  protected readonly labelText = computed(() =>
    this.state().required() ? `${this.label()} *` : this.label()
  );

  protected readonly displayText = computed(
    () => formatDateTimeValue(this.state().value()) || this.placeholder()
  );

  protected readonly showError = computed(
    () => this.state().touched() && this.state().errors().length > 0
  );

  // Holds a half-filled entry (a time without its date), which saves as a
  // cleared value until every part the input asks for is filled.
  private readonly parts = signal<DateTimeParts>({ date: null, time: null });
  protected readonly partsForm = form(this.parts, path =>
    disabled(path, { when: () => this.state().readonly() })
  );
  private readonly composed = computed(() =>
    composeDateTimeValue(this.parts(), this.dateField(), this.timeField())
  );

  constructor() {
    effect(() => {
      const saved = this.state().value() ?? null;
      untracked(() => {
        if (saved !== this.composed()) this.seed(saved);
      });
    });

    effect(() => {
      const value = this.composed();
      untracked(() => {
        const state = this.state();
        if (value === (state.value() ?? null)) return;
        state.value.set(value);
        state.markAsDirty();
      });
    });
  }

  ngOnInit(): void {
    this.seed(this.state().value() ?? null);
  }

  private seed(saved: string | null): void {
    const parts = toDateTimeParts(saved);
    if (parts) this.parts.set(parts);
  }
}
